import { FC, useEffect, useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CommonMenuListPage from "../../../components/CustomMenuListPage/CommonMenuListPage";
import panImage from "../../../assets/images/pan-image.png";
import editIcon from "../../../assets/icons/square-edit-outline.svg";
import lockIcon from "../../../assets/icons/lock.svg";
import EventIcon from "@mui/icons-material/Event";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAppSelector } from "../../../redux-store/CustomReduxHooks";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enUS } from "@mui/x-date-pickers/locales";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import { CleverTapEventType } from "../../../utils/CleverTapConstant";
import { CT_ScreenEvntDtls } from "../../Feature_CleverTap/ScreenEvents/CleverTapEventDetails";
import { SCREEN_TYPE } from "../../../utils/constants";
import EditProfilePopup from "./EditProfilePopup";
import { isEmpty } from "lodash";
import EditEmailPopup from "./EditEmailPopup";
import { Colors } from "../../../styles/constant";
import bulbIcon from "../../../assets/icons/famicons_bulb-sharp.svg";
import { useAppTranslation } from "../../../hooks/useAppTranslation";

dayjs.extend(customParseFormat);

interface Props {
  handleBackBtnClick: Function;
  pathname?: string;
}

const MyProfile: FC<Props> = ({ handleBackBtnClick, pathname }) => {
  const [editFieldName, setEditFieldName] = useState<string>("");
  const [editEmailPopupOpen, setEditEmailPopupOpen] = useState<boolean>(false);
  const [isEmailUpdateComplete, setIsEmailUpdateComplete] =
    useState<boolean>(false);
  const usLocale =
    enUS.components.MuiLocalizationProvider.defaultProps.localeText;
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const isNoHolding = useAppSelector(
    (state) => state.GeneralAppElementsSlice.isNoHolding,
  );
  const { t } = useAppTranslation("common");

  useEffect(() => {
    triggerCleverTapEvent(CleverTapEventType.SCREEN_EVENT, {
      ScreenName: CT_ScreenEvntDtls.R_GAE.gaMyProfileDetail.screenName,
      ScreenType: SCREEN_TYPE.DashboardScreenType,
    });
  }, []);

  const handleEditClick = (name: string, value: string) => {
    if (isNoHolding) {
      setEditFieldName(name);
    }
  };

  const handleEditEmailForETBCustomer = () => {
    setEditEmailPopupOpen(true);
  };

  return (
    <>
      <CommonMenuListPage
        title={t("hamburgerMenu.menuItems.myProfile")}
        handleBackBtnClick={handleBackBtnClick}
        pathname={pathname}
        isMyProfilePage
      >
        <Stack padding="0 1rem" rowGap="1.5rem">
          {!isNoHolding && (
            <>
              <TextField
                className="ue-input-mask"
                fullWidth
                label="PAN"
                name="pan"
                value={userDetails?.pan}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <img
                        src={isNoHolding ? editIcon : panImage}
                        alt="pan"
                        style={{ width: "1rem" }}
                      />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                onClick={() => {
                  handleEditClick("pan", userDetails?.pan || "");
                }}
              />
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={{
                  ...usLocale,
                  fieldMonthPlaceholder: (params) =>
                    params.contentType === "letter" ? "MMM" : "MM",
                }}
              >
                <TextField
                  fullWidth
                  label="DOB"
                  name="dob"
                  value={
                    userDetails?.dob
                      ? dayjs(userDetails?.dob).format("DD MMM YYYY")
                      : ""
                  }
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <EventIcon
                          sx={{ color: "rgb(46, 46, 46)", fontSize: "1rem" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  onClick={() => {
                    handleEditClick("dob", userDetails?.dob || "");
                  }}
                />
              </LocalizationProvider>{" "}
            </>
          )}

          <Box>
            <TextField
              fullWidth
              label="Email ID"
              name="emailId"
              value={userDetails?.emailId}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <img src={editIcon} alt="pan" style={{ width: "1rem" }} />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              onClick={() => {
                isNoHolding
                  ? handleEditClick("emailId", userDetails?.emailId || "")
                  : handleEditEmailForETBCustomer();
              }}
            />
            {isEmailUpdateComplete && (
              <Box
                display="flex"
                alignItems="center"
                padding=".625rem .5rem"
                marginTop=".5rem"
                sx={{ backgroundColor: "#E7FCF4", borderRadius: ".5rem" }}
              >
                <img src={bulbIcon} alt="bulb_icon" />
                <Typography
                  variant="h6"
                  color={Colors.green}
                  marginLeft=".625rem"
                >
                  {t("myProfile.successMessages.emailUpdateSuccess")}
                </Typography>
              </Box>
            )}
          </Box>
          <TextField
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <img src={lockIcon} alt="pan" style={{ width: "1rem" }} />
                </InputAdornment>
              ),
            }}
            fullWidth
            name="mobileNumber"
            label="Mobile Number"
            value={userDetails?.mobileNumber}
            InputLabelProps={{ shrink: true }}
            sx={{
              ".MuiInputBase-root": {
                backgroundColor: isNoHolding ? "#D5D7DE" : "#F1F3F6",
              },
            }}
          />
        </Stack>
      </CommonMenuListPage>
      {!isEmpty(editFieldName) && (
        <EditProfilePopup
          open={!isEmpty(editFieldName)}
          handleOpen={setEditFieldName}
          fieldName={editFieldName}
        />
      )}
      {editEmailPopupOpen && (
        <EditEmailPopup
          open={editEmailPopupOpen}
          handleOpen={setEditEmailPopupOpen}
          setEmailUpdateComplete={setIsEmailUpdateComplete}
        />
      )}
    </>
  );
};

export default MyProfile;
